// src/components/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Globe, Music } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#f8f6f3] border-t border-gray-200 px-6 sm:px-10 lg:px-40 py-10 text-[#5b4a3e]">
      {/* ปรับจาก grid-cols-4 เป็น grid-cols-12 เพื่อให้แบ่งสัดส่วนได้ละเอียดขึ้น */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
        
        {/* Logo / Project name (ลดความกว้างลงเหลือ 5/12 เพื่อให้ตรงกลางขยับมาใกล้ขึ้น) */}
        <div className="flex flex-col gap-3 md:col-span-12 lg:col-span-5">
          <h3 className="text-xl font-bold text-[#d8653b]">
            {t("footer.title", "Culture Reader @CNX")}
          </h3>
          <p className="text-sm leading-relaxed">
            {t(
              "footer.tagline",
              "A collaborative digital library by Thailand–Laos–Philippines to preserve and share cultural heritage through reading."
            )}
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-2">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/nl.chiangmai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#3b5998] hover:underline whitespace-nowrap"
              aria-label={t(
                "footer.aria.facebook",
                "ไปยัง Facebook ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
              )}
            >
              <Facebook size={16} /> {t("footer.links.facebook", "Facebook")}
            </a>

            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@nl.cnx?is_from_webapp=1&sender_device=pc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#010101] hover:text-[#d8653b] transition-colors whitespace-nowrap"
              aria-label={t(
                "footer.aria.tiktok",
                "ไปยัง TikTok ของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
              )}
            >
              <Music size={16} /> {t("footer.links.tiktok", "TikTok")}
            </a>

            {/* Main site */}
            <a
              href="https://www.finearts.go.th/chiangmailibrary"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#7bae6a] hover:text-[#d8653b] transition-colors whitespace-nowrap"
              aria-label={t(
                "footer.aria.mainSite",
                "ไปยังเว็บไซต์หลักของหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่"
              )}
            >
              <Globe size={16} />
              {t("footer.links.mainSite", "Main website")}
            </a>
          </div>
        </div>

        {/* About (ปรับสัดส่วนเป็น 3/12) */}
        <div className="flex flex-col gap-3 md:col-span-5 lg:col-span-3">
          <h4 className="font-semibold text-[#111518]">
            {t("footer.sections.about.title", "About")}
          </h4>
          <Link className="text-sm hover:underline" to="/about">
            {t("footer.sections.about.mission", "Mission & Project")}
          </Link>
          <Link className="text-sm hover:underline" to="/volunteer-history">
            {t("footer.sections.about.volunteer", "Volunteer History")}
          </Link>
          <Link className="text-sm hover:underline" to="/contact">
            {t("footer.sections.about.contact", "Contact")}
          </Link>
        </div>

        {/* Resources & Icons (ขยายพื้นที่เป็น 4/12 เพื่อให้วาง 4 รูปในแถวเดียวได้) */}
        <div className="flex flex-col justify-start md:col-span-7 lg:col-span-4">
          {/* เพิ่ม lg:flex-nowrap เพื่อบังคับไม่ให้ปัดบรรทัดเมื่อจอใหญ่ */}
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 xl:gap-3">
            {/* ภาพที่ 1 (ขนาด 1:1) */}
            <img 
              src="/footer/1.png" 
              alt="Icon 1" 
              className="h-10 w-10 lg:h-12 lg:w-12 xl:h-16 xl:w-16 object-contain" 
            />
            {/* ภาพที่ 2 (ขนาด 1:1) */}
            <img 
              src="/footer/2.jpg" 
              alt="Icon 2" 
              className="h-10 w-10 lg:h-12 lg:w-12 xl:h-16 xl:w-16 object-contain" 
            />
            {/* ภาพที่ 3 (ขนาด 1:1) */}
            <img 
              src="/footer/3.jpg" 
              alt="Icon 3" 
              className="h-10 w-10 lg:h-12 lg:w-12 xl:h-16 xl:w-16 object-contain" 
            />
            {/* ภาพที่ 4 (ขนาด 2:1) */}
            <img 
              src="/footer/4.png" 
              alt="Icon 4" 
              className="h-10 w-20 lg:h-12 lg:w-24 xl:h-16 xl:w-32 object-contain bg-gray-200 rounded" 
            />
          </div>
        </div>

      </div>

      <div className="max-w-7xl mx-auto border-t border-gray-300 mt-10 pt-6 text-center text-xs text-gray-500">
        {t("footer.copyright", {
          year,
          defaultValue:
            "© {{year}} Culture Reader @NL.CNX — ความร่วมมือกับหอสมุดแห่งชาติรัชมังคลาภิเษก เชียงใหม่ สงวนลิขสิทธิ์",
        })}
      </div>
    </footer>
  );
}